/* AuditPolicyDetailsViewComponent.js */

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./AuditPolicyDetailsViewRenderer");
const Steve = require("@gluenetworks/steve_core").getInstance();
const {ruleLabelsToUuids, COMMAND_SET_SENTINELS} = require("../../utils/ruleLabelMapper");
const {buildQueryBuilderColumns} = require("../../utils/queryBuilderColumns");
const {buildExpressionFieldNames} = require("../../utils/deviceFilterColumns");

class AuditPolicyDetailsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        MUF,
        steveIo,
        DeviceEntityDef,
        CustomFieldEntityDef,
        SiteEntityDef,
        CommandSetEntityDef,
        GitService,
        viewState,
        TagsEntityDef,
        policy,
        readOnly,
        isChildView
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._userSession = userSession;
        this._steveIo = steveIo;
        this._deviceEntityDef = DeviceEntityDef;
        this._customFieldEntityDef = CustomFieldEntityDef;
        this._siteEntityDef = SiteEntityDef;
        this._commandSetEntityDef = CommandSetEntityDef;
        this._gitService = GitService;
        this._viewState = viewState;
        this._tagsEntityDef = TagsEntityDef;
        this._tags = [];
        this._tagIdToNameMap = {};
        this._commitHash = this._viewState.commitHash;
        this._commandSetFeed = {};
        this.cmdSetValLabelMap = {
            all: "All",
        };

        this.cmdSetLabelValMap = {
            All: "all",
        };

        this._userDataStorage = this._gameEditor._userDataStorage;

        this._auditPolicy = undefined;
        this._policy = policy;
        this.readOnly = readOnly;
        this._isReadOnly = false;
        this._osMap = undefined;
        this._isChildView = isChildView;
        this._permLevels = [];
        this._commandSetFeed = {};

        // Custom field metadata for the JSON edit mode (UUID↔label mapping for
        // customFieldValues.<uuid> field paths and enum-option UUIDs in
        // ifExpression / thenExpression / elseExpression leaves). Populated in
        // whenModelIsReady(); defaults to {} so callers can always read it.
        this._customFieldsWithInherited = {};
        this._expressionFieldNames = [];
        this._customFieldsEntityInstance = undefined;

        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerPolicyListeners() {
        this._auditPolicy.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
        this._auditPolicy.on(
            "PendingPatchesChanged",
            (this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this))
        );
        this._auditPolicy.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterPolicyListeners() {
        this._auditPolicy.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._auditPolicy.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._auditPolicy.removeListener(
            "PristinePropertiesChanged",
            this._onPristinePropertiesChanged
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get steveIo() {
        return this._steveIo;
    }

    getAuditPolicy() {
        return this._auditPolicy;
    }

    get auditPolicy() {
        return this.getAuditPolicy();
    }

    set auditPolicy(newPolicy) {
        this._auditPolicy = newPolicy;
        return true;
    }

    get auditPolicyRules() {
        return this.getAuditPolicyRules();
    }

    getAuditPolicyRules() {
        return this.auditPolicy.getRules();
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer
            .shutdown()
            .then(() => {
                if (!this._policy && !_.isUndefined(this._auditPolicy)) {
                    this._unregisterPolicyListeners();
                } else {
                    return Promise.resolve();
                }
            })
            .then(() =>
                !_.isEmpty(this._commandSetFeed)
                    ? this._commandSetFeed.shutdown()
                    : Promise.resolve()
            )
            .then(
                () => {
                    //this._modalUserFeedback = undefined;
                    this._auditPolicy = undefined;
                    this._steveIo = undefined;
                    this._logger = undefined;
                    this._gameEditor = undefined;
                    this._renderer = undefined;
                    this._isReadOnly = undefined;
                    this._permLevels = undefined;
                    this._osMap = undefined;
                    this._tagsEntityDef = undefined;
                    this._tags = undefined;
                    this._tagIdToNameMap = undefined;
                },
                (err) => {
                    this._logger.warn(err, "PolicyDetailsViewComponent.shutdown() error.");
                }
            );
    }

    _getCommitFileContent(commitHash) {
        return Promise.resolve()
            .then(() =>
                this._gitService.getFileContent({
                    name: this._gameEditor.getAuditPolicy().name,
                    type: "ConfigAuditPolicy",
                    orgId: this._userSession.activeOrganization.id,
                    hash: commitHash,
                })
            )
            .catch((err) => {
                this._logger.warn(
                    {error: err, name: this._gameEditor.getAuditPolicy().name, hash: commitHash},
                    "AuditPolicyDetailsViewComponent: could not retrieve committed policy file; skipping git reconciliation and opening with current data."
                );
                return undefined;
            });
    }

    get readOnlyFieldIds() {
        return [
            "latestHaveIChanged",
            "lastAuditBy",
            "lastAuditDeviceCount",
            "lastAuditDeviceErrorCount",
            "lastAuditDeviceSuccessCount",
            "lastAuditDevicesInViolationCount",
            "lastAuditStatus",
            "lastAuditTime",
            "lastAuditViolationCount",
            "lastCommitHash",
            "lastAuditDevicesSkippedCount",
        ];
    }

    get commitApplyInclusionFields() {
        return ["name", "description", "rules", "permissionLevel"];
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);

        // While the JSON pane has an unapplied draft, the renderer's JSON
        // Discard veil owns the close guard — defer to it so an entity dirty
        // event (e.g. the form-flush on entering JSON mode) can't replace it
        // with the form "Save or Discard" veil. (No recursion: with a draft
        // active, _refreshJsonLeaveGuard arms the JSON guard directly.)
        const renderer = this._renderer;
        if (renderer && renderer._jsonMode && renderer._jsonHasDraft) {
            renderer._refreshJsonLeaveGuard();
            return;
        }

        const rejection = () => {
            return Promise.resolve(this._auditPolicy.clearPendingPatches(true))
                .then(() => this._modalUserFeedback.hideDisplayed())
                .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                .then(() => undefined);
        };

        const onBeforeUnloadFunc = () => {
            // const form = this._renderer._formsComponent.getValue();
            // if(form.rules.length > 70) {
            //     return rejection();
            // }

            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    if (this.isPolicyFormValid(true)) {
                        return this.savePolicy(false).then(() => undefined); // it will not close the tab if it will have something in result
                    }
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return rejection();
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this._auditPolicy.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged() {
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('PolicyPropertiesChanged');
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("PolicyPropertiesChanged", pristineProperties, before);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getPermLevels() {
        const authorizedActions = this._userSession.authorizedActions;
        const perms = ["auditManageLevel1", "auditManageLevel2", "auditManageLevel3"];
        const res = [];
        perms.forEach((perm) => {
            if (authorizedActions.findIndex(perm) >= 0) {
                res.push(perm);
            }
        });
        return res;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDirty() {
        return this._auditPolicy.isDirty();
    }

    /**
     * Best-effort apply of a parsed-from-JSON policy object onto the live
     * entity. Used when toggling JSON Edit → Form Edit. Converts each rule's
     * labelized UUIDs back to their canonical UUID form via ruleLabelsToUuids.
     *
     * The JSON view round-trips every semantic field (queryBuilder leaf/group
     * internals and rule-top-level conceptItemId/rsiId are no longer stripped,
     * and generated leaf props are backfilled on parse-back), so an unedited
     * rule diffs byte-identical against the entity's pristine baseline — no
     * snapshot substitution is needed to keep the SlickGrid dirty flag accurate.
     *
     * @returns {Array<{path,message}>} non-fatal errors (unresolvable labels);
     *          the caller blocks the toggle and retains the draft when non-empty.
     */
    applyParsedPolicy(parsedPolicy) {
        if (!parsedPolicy || typeof parsedPolicy !== "object") {
            return [];
        }
        const labelMaps = this.getRuleLabelMaps();
        const labelResolutionErrors = [];

        // Route through the entity's setters so each write goes through
        // setProperty → jsonPatch.compare → pendingPatches; direct field
        // assignment bypasses the patch machinery and leaves
        // getProperties() returning the pristine value, which would cause
        // the post-toggle form refresh to keep showing the pre-edit text.
        if (typeof parsedPolicy.name === "string") {
            this._auditPolicy.setName(parsedPolicy.name.trim());
        }
        if (typeof parsedPolicy.description === "string") {
            this._auditPolicy.setDescription(parsedPolicy.description);
        }
        if (typeof parsedPolicy.permissionLevel === "string") {
            this._auditPolicy.setPermissionLevel(parsedPolicy.permissionLevel);
        }
        if (Array.isArray(parsedPolicy.rules)) {
            const convertedRules = [];
            parsedPolicy.rules.forEach((rule, ruleIndex) => {
                const {rule: ruleWithUuids, errors: ruleErrors} = ruleLabelsToUuids(
                    rule,
                    labelMaps
                );
                if (ruleErrors && ruleErrors.length) {
                    ruleErrors.forEach((ruleError) =>
                        labelResolutionErrors.push({
                            path: `/rules/${ruleIndex}${ruleError.path}`,
                            message: ruleError.message,
                        })
                    );
                }
                convertedRules.push(ruleWithUuids);
            });
            this._auditPolicy.setRules(convertedRules);
        }
        return labelResolutionErrors;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    whenModelIsReady() {
        return Promise.all([
            this.gameEditor.whenPolicyIsReady(),
            this.gameEditor.whenConceptItemsAreReady(),
        ])
            .then(() => this._getVendorMapDynamic())
            .then(() => this._getTypeBaseMapDynamic())
            .then(() => this._getOSMapDynamic())
            .then(() => this._getCommandSets())
            .then(() => this._loadCustomFields())
            .then(() => this._loadTags())
            .then(() => this._loadExpressionFieldNames())
            .then(() => {
                this._auditPolicy = this._policy || this.gameEditor.getAuditPolicy();
                this._permLevels = this._getPermLevels();
                if (
                    this._auditPolicy.lastCommitHash &&
                    Steve.controlAppNav._versionControlLocalEnabled
                ) {
                    return this._getCommitFileContent(this._auditPolicy.lastCommitHash);
                }
                return Promise.resolve();
            })
            .then((lastCommitFileContent) => {
                if (lastCommitFileContent) {
                    this._lastCommitUpdatedOn = lastCommitFileContent.updatedOn;
                }
                if (this._commitHash && Steve.controlAppNav._versionControlLocalEnabled) {
                    return this._getCommitFileContent(this._commitHash);
                } else {
                    return Promise.resolve();
                }
            })
            .then((commitFileContent) => {
                if (this._commandSetFeed && this._commandSetFeed.cache) {
                    Object.keys(this._commandSetFeed.cache).forEach((cmdSetId) => {
                        this.cmdSetValLabelMap[cmdSetId] =
                            this._commandSetFeed.cache[cmdSetId].getName();
                        this.cmdSetLabelValMap[this._commandSetFeed.cache[cmdSetId].getName()] =
                            cmdSetId;
                    });
                }
                if (commitFileContent) {
                    // Generating these only when applying a commit
                    const conceptItemLabelValues = Object.values(
                        this.gameEditor.conceptItems
                    ).reduce((conceptItemLabelValueAccumulator, conceptItem) => {
                        conceptItemLabelValueAccumulator[conceptItem.label] = conceptItem.value;
                        return conceptItemLabelValueAccumulator;
                    }, {});
                    const rsiIdsMap = this.gameEditor._rsiIds.reduce((acc, item) => {
                        acc[item.label] = item.value;
                        return acc;
                    }, {});

                    Object.keys(this._auditPolicy.getProperties()).forEach((key) => {
                        if (this.commitApplyInclusionFields.indexOf(key) >= 0) {
                            // If both are falsy, no need to update property key and risk dirty state
                            if (this._auditPolicy[key] || commitFileContent[key]) {
                                if (key === "rules") {
                                    commitFileContent[key].forEach((rule) => {
                                        if (rule.commandSetName) {
                                            rule.commandSet =
                                                this.cmdSetLabelValMap[rule.commandSetName];
                                            delete rule.commandSetName;
                                        }
                                        if (rule.conceptItem) {
                                            rule.conceptItemId =
                                                conceptItemLabelValues[rule.conceptItem];
                                            delete rule.conceptItem;
                                        }
                                        if (rule.restSyncItem) {
                                            rule.rsiId = rsiIdsMap[rule.restSyncItem];
                                            delete rule.restSyncItem;
                                        }

                                        if (rule.commandSetResult) {
                                            if (rule.commandSetResult.conceptItem) {
                                                rule.commandSetResult.conceptItemId =
                                                    conceptItemLabelValues[
                                                        rule.commandSetResult.conceptItem
                                                    ];
                                                delete rule.commandSetResult.conceptItem;
                                            }
                                            if (rule.commandSetResult.restSyncItem) {
                                                rule.commandSetResult.rsiId =
                                                    rsiIdsMap[rule.commandSetResult.restSyncItem];
                                                delete rule.commandSetResult.restSyncItem;
                                            }
                                        }
                                    });
                                }
                                this._auditPolicy[key] = commitFileContent[key];
                            }
                        }
                    });
                }
                this._isReadOnly = this.readOnly || false;
                if (this._permLevels.indexOf(this._auditPolicy.permissionLevel) < 0) {
                    this._isReadOnly = true;
                }
                const rules = _.clone(this._auditPolicy.rules);
                let isUpdated = false;
                rules.forEach((rule) => {
                    if (!rule.os && rule.os !== "") {
                        rule.os = "";
                        isUpdated = true;
                    }
                    if (!rule.conceptItemId && rule.conceptItemId !== null) {
                        rule.conceptItemId = null;
                        isUpdated = true;
                    }
                });
                if (isUpdated && !this._isChildView) {
                    this.auditPolicy.setRules(rules);
                }
            })
            .then(() => {
                if (this._auditPolicy.pendingPatches && !this._commitHash) {
                    return this._auditPolicy.update({skipAudit: true}).catch((err) => {
                        if (err && err.errorCode === "NotAuthorized") {
                            this._logger.warn(
                                err,
                                "AuditPolicyDetailsViewComponent.whenModelIsReady() -> updateNoAudit() NotAuthorized"
                            );
                            return Promise.resolve();
                        } else {
                            return Promise.reject(err);
                        }
                    });
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                if (!this._policy) {
                    this._unregisterPolicyListeners();
                    this._registerPolicyListeners();
                    return Promise.resolve();
                }
            })
            .catch((err) => {
                this._logger.warn(err, "AuditPolicyDetailsViewComponent.whenModelIsReady() error");
                this._modalUserFeedback.displayError({
                    errorMsg: "This policy cannot be opened.",
                    errorCode: "",
                });
                return Promise.reject(err);
            });
    }

    resetPolicyProperties() {
        this._auditPolicy.clearPendingPatches();
        this._commitHash = undefined;
        return Promise.resolve();
    }

    isPolicyFormValid(rollbackInvalidData = false) {
        const policy = this._auditPolicy;
        const inputChanges = policy.getProperties();
        const oldData = policy.getPristineProperties();
        const inputName = inputChanges.name.trim();
        const isInputNameEmpty = inputName.length === 0;

        if (isInputNameEmpty && rollbackInvalidData === false) {
            throw {
                errorMsg: "Name cannot be left empty. Please enter a valid policy name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        } else if (isInputNameEmpty && rollbackInvalidData === true) {
            policy.setName(oldData.name.trim());
            return true;
        } else {
            policy.setName(inputName);
            return true;
        }
    }

    savePolicy() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._auditPolicy.isDirty()) {
                    this.isPolicyFormValid();
                    const rules = this._auditPolicy.getRules() || [];
                    rules.forEach((rule) => {
                        if (!rule.os && rule.os !== "") {
                            rule.os = "";
                        }
                        if (!rule.conceptItemId && rule.conceptItemId !== null) {
                            rule.conceptItemId = null;
                        }
                    });
                    this._auditPolicy.setRules(rules);
                    return this._auditPolicy.update();
                }
            })
            .catch((err) => {
                this._logger.warn({error: err}, "PolicyDetailsViewComponent.savePolicy() error.");
                if (err.errorCode === "NoPendingUpdates") {
                    this.emit("PolicyPropertiesChanged");
                    return Promise.reject();
                } else {
                    let errorObj;
                    if (err.validationErrors) {
                        errorObj = Object.assign({}, err);
                        err.validationErrors.forEach((er) => {
                            if (typeof er === "string") {
                                errorObj.errorMsg += `\n Error: ${er}`;
                            } else {
                                errorObj.errorMsg += `\n Rule: ${er.ruleName}, Error: ${er.msg}`;
                            }
                        });
                    } else {
                        errorObj = err;
                    }
                    return this._modalUserFeedback.displayError(errorObj);
                }
            });
    }

    async _getVendorMapDynamic() {
        this._vendorMap = await this._deviceEntityDef.loadAndMergeVendorMap(
            this._gameEditor._activeOrgId
        );
        return Promise.resolve();
    }

    async _getTypeBaseMapDynamic() {
        this._typeBaseMap = await this._deviceEntityDef.loadAndMergeTypeBaseMap(
            this._gameEditor._activeOrgId
        );
        return Promise.resolve();
    }

    async _getOSMapDynamic() {
        this._osMap = await this._deviceEntityDef.loadAndMergeOsMap(this._gameEditor._activeOrgId);
        return Promise.resolve();
    }

    /**
     * Load the device custom fields (own + inherited) for the active org.
     * Used by the JSON edit mode to label customFieldValues.<uuid> paths and
     * enum option UUIDs inside expressions. Mirrors the loader on
     * EditAuditPolicyComponent so the per-rule subview can reuse this map
     * (set via setCustomFieldsEntity / _customFieldsWithInherited).
     */
    async _loadCustomFields() {
        if (!this._customFieldEntityDef) {
            return Promise.resolve({});
        }
        try {
            const customFieldsEntity = await this._customFieldEntityDef.retrieveCustomFields(
                this._gameEditor._activeOrgId
            );
            this._customFieldsEntityInstance = customFieldsEntity;
            const inheritedFields = await customFieldsEntity.loadInheritedFields();
            Object.assign(
                this._customFieldsWithInherited,
                customFieldsEntity.fields || {},
                inheritedFields || {}
            );
        } catch (loadError) {
            this._logger.warn(
                {error: loadError},
                "AuditPolicyDetailsViewComponent._loadCustomFields() error."
            );
        }
        return this._customFieldsWithInherited;
    }

    /**
     * Load the org's tags and build an id→name map, used to convert
     * ifExpression `tagIds` condition values to tag names on policy download
     * (see AuditPolicyDetailsViewRenderer._processRuleForUUID). On failure the
     * map is left empty so download degrades to leaving raw ids rather than
     * failing the export.
     */
    async _loadTags() {
        try {
            this._tags = (await this._tagsEntityDef.loadTags(this._gameEditor._activeOrgId)) || [];
            this._tagIdToNameMap = this._tags.reduce((map, tag) => {
                map[tag.id] = tag.name;
                return map;
            }, {});
        } catch (loadError) {
            this._logger.warn(
                {error: loadError},
                "AuditPolicyDetailsViewComponent._loadTags() error — tag names omitted from export."
            );
            this._tags = [];
            this._tagIdToNameMap = {};
        }
        return this._tagIdToNameMap;
    }

    /**
     * Load the device node-prop defs and assemble the set of field names valid
     * in a rule's ifExpression (base device columns + nodeProps.<id>). Used by
     * the JSON edit mode to flag unrecognized expression fields. On failure the
     * set is left empty so validation degrades to a no-op rather than
     * false-flagging valid fields.
     */
    async _loadExpressionFieldNames() {
        try {
            const constructTypeEntityDef =
                this._gameEditor && this._gameEditor._constructTypeEntityDef;
            let nodeProps = {};
            if (constructTypeEntityDef && constructTypeEntityDef.getDeviceNodePropDefs) {
                nodeProps = await constructTypeEntityDef.getDeviceNodePropDefs(
                    this._gameEditor._activeOrgId
                );
            }
            this._expressionFieldNames = buildExpressionFieldNames(nodeProps);
        } catch (loadError) {
            this._logger.warn(
                {error: loadError},
                "AuditPolicyDetailsViewComponent._loadExpressionFieldNames() error — ifExpression field validation degraded."
            );
            this._expressionFieldNames = [];
        }
        return this._expressionFieldNames;
    }

    getExpressionFieldNames() {
        return this._expressionFieldNames || [];
    }

    /**
     * Build the maps consumed by ruleLabelMapper. Centralized here so both
     * the policy-level JSON pane and the per-rule subview see the same view
     * of UUID↔label data.
     */
    getRuleLabelMaps() {
        const commandSetUuidToName = {};
        // commandSetOs lets the parse-back resolver scope a command-set name to
        // the rule's OS, avoiding collisions when two OS families share a name.
        const commandSetOs = {};
        if (this._commandSetFeed && this._commandSetFeed.cache) {
            Object.keys(this._commandSetFeed.cache).forEach((commandSetId) => {
                commandSetUuidToName[commandSetId] =
                    this._commandSetFeed.cache[commandSetId].getName();
                commandSetOs[commandSetId] = this._commandSetFeed.cache[commandSetId].getOs();
            });
        }
        // cmdSetValLabelMap seeds only the "all" sentinel; merge the full
        // sentinel set so allConfig/allOperational round-trip and validate.
        Object.assign(commandSetUuidToName, COMMAND_SET_SENTINELS, this.cmdSetValLabelMap || {});
        return {
            conceptItems: (this._gameEditor && this._gameEditor.conceptItems) || {},
            rsiIdsMap: (this._gameEditor && this._gameEditor._rsiIdsMap) || {},
            commandSets: commandSetUuidToName,
            commandSetOs,
            customFields: this._customFieldsWithInherited || {},
            tags: this._tagIdToNameMap || {},
            qbColumns: buildQueryBuilderColumns(),
        };
    }

    async _getCommandSets() {
        const query = {};
        const activeOrgId = this._gameEditor._activeOrgId;
        this._commandSetFeed = this._commandSetEntityDef.startCommandSetsFeed(
            activeOrgId,
            query,
            {
                name: true,
                id: true,
                os: true,
                dataType: true,
                commands: true,
                conceptItemIds: true,
                rsiIds: true,
                getUrls: true,
                basicMode: true,
            },
            {},
            {skip: 0, limit: 200}
        );

        return this._commandSetFeed.whenInitialized();
    }

    // getPermLevels() {
    //     return
    // }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = AuditPolicyDetailsViewComponent;

AuditPolicyDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.entities.Device",
    "$teve.entities.CustomFields",
    "$teve.entities.Site",
    "$teve.entities.CommandSet",
    "$teve.services.Git",
    "gameEditor.viewState",
    "$teve.entities.Tags",
];

AuditPolicyDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "Policy Details",
    icon: "configdrift.audit_policy_details",
    shown: true,
    title: "Details",
    order: 1,
};
